import type { AskReport, Assumed, ReportTopic, StandardCandidate } from './types';
import type { Profile } from './profile';

/**
 * Whether a standard sits outside the grades the teacher is working in.
 *
 * Only ever true when both sides are known. The grade filter sent to the corpus is optional —
 * a teacher who gave no grade, and a model that inferred none, send none — so a fractions
 * query can return the grade 5 standards to someone teaching grade 7. That is a reasonable
 * thing for the corpus to do and a bad thing to show without saying, which is the whole
 * purpose of this function: the report says whose grade a standard is, rather than implying
 * it is the reader's.
 */
export function outsideRequestedGrades(standardGrades: string[] | undefined, requested: string[]): boolean {
  if (!standardGrades?.length || !requested.length) return false;
  return !standardGrades.some((grade) => requested.includes(grade));
}

/** "Grade 5", "Grades 6–7", or nothing to say. */
export function gradeLabel(grades: string[] | undefined): string {
  if (!grades?.length) return '';
  return grades.length === 1 ? `Grade ${grades[0]}` : `Grades ${grades.join(', ')}`;
}
export function buildReport(ask: { id: string; text: string; profile: Profile; createdAt: string }, standards: (StandardCandidate & { reason: string })[], topicsByStandard: Map<string, ReportTopic[]>, assumed: Assumed, providers?: AskReport['providers']): AskReport {
  return { ...ask, status: 'complete', assumed, providers, standards: standards.map((s, position) => {
    const topics = topicsByStandard.get(s.id) ?? [];
    // Explicit on every standard so the report never has to guess what a missing source meant.
    const labelled = s.source === 'uploaded' ? s : { ...s, source: 'learning-commons' as const };
    return { ...labelled, position, gap: topics.length === 0, topics };
  }) };
}
